import type { ApiOptions } from "@rudderstack/analytics-js";

import type {
  issue__AlternativePaymentOption as AlternativeStrategy,
  AuthenticateResultErrorType,
} from "@medbillai/graphql-types";

import type { ExitError } from "@medbillai/ui";

import type { CaseListFilters } from "../../screens/cases/utils/types";
import type { OnboardingFlowVersion } from "../onboarding/types";
export type PanelSource = "nav_sidebar" | "case_header" | "overlay_dismiss";
export type PanelToggleAction = "open" | "close";
export type CaseListFilterType = "open" | "unread" | "read" | "closed";

export const defaultSessionReplaySampleRate = 0.3;

export type AnalyticsConfig = {
  writeKey: string;
  dataPlaneUrl: string;
  // when running in debug mode, events will be logged to the console
  debug?: boolean;
  // Amplitude API key for session replay
  amplitudeApiKey?: string;
  // Session replay configuration
  sessionReplay?: {
    sampleRate?: number; // Percentage of sessions to capture (0-1)
    enabled?: boolean;
  };
};

export const defaultConfig: ApiOptions = {
  integrations: {
    All: true,
    "Facebook Conversions": false,
    "Google Ads Enhanced conversions Mobile App": false,
  },
};

export const trackingConfig: ApiOptions = {
  integrations: {
    All: true,
    "Facebook Conversions": true,
    // c is lower case to match "Display Name"
    "Google Ads Enhanced conversions Mobile App": true,
  },
};

type Endpoint = "FLEXPA" | "WEB_AGENT";

type FakeDoorMmfaEventBase = {
  linkCategory?: "INSURANCE" | "EHR";
  variant: "connect_existing" | "create_email";
};
export type InsuranceLinkPage =
  | "RELINK"
  | "LOGIN"
  | "MFA_CODE"
  | "SECURITY_QUESTION"
  | "SELECT_MFA"
  | "MANAGED_MFA_APPROVAL"
  | "SUCCESS"
  | "ERROR";

export enum LogoutReason {
  USER_LOGOUT = "user_logout",
  UNAUTHORIZED = "unauthorized",
  AUTH_ERROR = "auth_error",
  FORCED_LOGOUT = "forced_logout",
  SHADOW_LOGOUT = "shadow_logout",
  ACCOUNT_DELETION = "account_deletion",
  UNKNOWN = "unknown",
}

/** Steps in the BillPayAgreementModal wizard flow. */
export type BillPayStepId =
  | "howItWorks"
  | "savingsPreference"
  | "alternativeStrategies"
  | "successFee";

// Shared shape for every Bill Pay v2 funnel event. `eventId` is the
// BillPayRequestedEvent id, joinable to the domain event in Hex.
type BillPayEventBase = { issueId: string; eventId: string };

export type EventTypes = {
  // Application lifecycle
  ClientVersionMismatch: {
    clientBinary: "OLD" | "NEW";
    clientVersion: string;
    apiVersion: string;
  };
  StartedEASUpdate: {
    clientVersion: string;
    easUpdateId: string;
    isLatestVersion: boolean;
  };
  EASUpdateMismatch: {
    clientVersion: string;
    supportedUpdateId: string | null;
    currentUpdateId: string | null;
    availableUpdateId: string | null;
    isLatestVersion: boolean;
  };
  EASUpdateNavigating: {
    authIsReady: boolean;
  };
  EASUpdateNavigated: {
    directive: "TIMEOUT" | "DIRECT";
  };
  NoUpdateAvailable: {
    clientVersion: string;
    latestVersion: string;
  };
  // SubmitLogin is used to track the method of login used by the user, after
  // initiating either an email or oauth login.
  SubmitLogin: {
    method: "EMAIL" | "OAUTH";
  };
  FaceIdLogoutButton: Record<string, never>;
  FaceIdVerifyButton: Record<string, never>;
  // Verification on the authentication screen for a magic link login
  EmailVerificationLinkError: {
    type:
      | "INVALID_PKCE_CODE_CHALLENGE"
      | "MISSING_TOKEN"
      | "UNABLE_TO_AUTH_MAGIC_LINK"
      | "UNKNOWN_ERROR";
  };
  // Verification on the authentication screen for an OAuth login
  OAuthVerificationError: {
    type: "INVALID_AUTH_OAUTH" | "UNKNOWN_ERROR";
  };
  AuthenticateError: {
    type: AuthenticateResultErrorType;
  };
  ClickedTab: { tabName: string };
  ClickedSettings: Record<string, never>;
  OnboardingBackStep: { initialRoute: string };
  // Called any time we redirect to the login screen with a forceLogout param
  // The reason depends on the context of the redirect. We set it to shadow_logout
  // when we log out from shadow user mode so that can be used to filter out logouts
  // from shadow users.
  Logout: { reason: LogoutReason };

  // Notifications
  // Called when we initiate the permission prompt
  ClickedEnableNotifications: Record<string, never>;
  // Called when the user has already denied notifications
  ClickedEnableNotificationsAlreadyDenied: Record<string, never>;
  // Called when the user allows notifications
  ClickedEnableNotificationsSuccess: Record<string, never>;
  // Called when the user denies notifications
  ClickedEnableNotificationsDenied: Record<string, never>;
  // Called when there was an error initiating or displaying the permission prompt
  ClickedEnableNotificationsError: Record<string, never>;
  // Called when we initiate the disable permission prompt from settings screen
  ClickedDisableNotifications: Record<string, never>;
  // Called when the user skips the notification permission prompt from settings screen
  ClickedSkipNotifications: Record<string, never>;
  // Called when we show the notification error alert
  ShowedNotificationErrorAlert: Record<string, never>;
  // Show notifications prompt for case escalation
  ShowedNotificationPromptForCaseEscalation: Record<string, never>;
  // Web to mobile modal shown when case is escalated
  WebToMobileModalShown: Record<string, never>;
  // Web to mobile modal dismissed by user
  WebToMobileModalDismissed: Record<string, never>;
  // Web to mobile modal auto-dismissed after timeout
  WebToMobileModalAutoDismissed: Record<string, never>;

  // Notification prompt buttons
  // Called when the user presses the allow button in the notification prompt
  ClickedAllowNotifications: Record<string, never>;
  // Called when the user presses the dismiss button in the notification prompt
  ClickedDismissNotifications: Record<string, never>;

  // Called when the user submits a HIPAA release
  StartHipaaRelease: Record<string, never>;
  SignHipaaRelease: Record<string, never>;
  CompletedHipaaRelease: Record<string, never>;
  FailedHipaaRelease: Record<string, never>;
  SubmitHipaaRelease: Record<string, never>;
  HipaaReleaseFormStepForward: { page: string };
  HipaaReleaseDependentCheck: { dependent: "YES" | "NO" };
  HipaaReleaseSubscriberCheck: { subscriber: "YES" | "NO" };
  HipaaReleasePatientSelected: { newPatient: "YES" | "NO" };
  HipaaReleaseSubscriberSelected: { newSubscriber: "YES" | "NO" };

  // Chat
  CreateIssue:
    | { source: "CASE_SCREEN" | "CASE_INTAKE_FLOW" | "BILL_DISPUTE" }
    | {
        source: "WELCOME_SCREEN";
        action:
          | "EXPLAIN_BENEFITS"
          | "REVIEW_BILLS"
          | "GET_STARTED"
          | "REVIEW_BILLS_NEED_HELP_WITH_SPECIFIC_BILL"
          | "HELP_WITH_BILL"
          | "UPLOAD_BILL_CTA";
      };
  SentMessage: { issueId: string };
  OpenedIssue: { id: string; source: string };
  ClickedUpload: { type: "FILE" | "GALLERY" | "CAMERA" };
  ClickedChatEvent: {
    type:
      | "DOCUMENT_UPLOADED"
      | "HIPAA_RELEASE_REQUESTED"
      | "TEMPLATE_RELEASE_REQUESTED";
  };
  HipaaReleaseSigned: { templateId: string; organizationSlug?: string };
  DocumentUploadStarted: {
    source: "CASE_INPUT" | "DOCUMENT_UPLOADED_EVENT";
  };
  PreAuthBillUploadFileSelected: {
    source: "BUTTON" | "DRAG_AND_DROP";
  };
  PreAuthCreateCaseClicked: Record<string, never>;
  // For reporting the order of CTA buttons on the home screen
  HomeScreenCTAOrderViewed: {
    ctaOrder: string[];
  };
  // For tracking the order of pill prompts shown to the user
  PillPromptsShown: {
    pillPromptsOrder: string[];
    caseType: string;
  };

  // Insurance Linking Steps - Web Agent
  // NOTE: Use useAccountLinkAnalytics() hook to track these events.
  // The hook automatically adds linkCategory: 'INSURANCE' | 'EHR' based on the flow.
  WebAgentCredentials: { linkCategory?: "INSURANCE" | "EHR" };
  WebAgentMfaSelection: {
    preferredMethod: string | undefined;
    linkCategory?: "INSURANCE" | "EHR";
  };
  WebAgentSecurityQuestion: { linkCategory?: "INSURANCE" | "EHR" };
  WebAgentMfaCode: { linkCategory?: "INSURANCE" | "EHR" };
  WebAgentResendMfaCode: { linkCategory?: "INSURANCE" | "EHR" };
  WebAgentManagedMfaApproval: {
    approved: boolean;
    linkCategory?: "INSURANCE" | "EHR";
  };

  // Flexpa Linking Steps
  // NOTE: Use useAccountLinkAnalytics() hook to track these events.
  // The hook automatically adds linkCategory: 'INSURANCE' | 'EHR' based on the flow.
  FlexpaCheckSupport: {
    planSupported: boolean;
    linkCategory?: "INSURANCE" | "EHR";
  };
  FlexpaUnsupportedPlan: {
    insuranceName: string | undefined;
    linkCategory?: "INSURANCE" | "EHR";
  };

  // Insurance Linking General
  // NOTE: Use useAccountLinkAnalytics() hook to track these events.
  // The hook automatically adds linkCategory: 'INSURANCE' | 'EHR' based on the flow.
  /** Should be fired any time we open the insurance link flow
   * source is where in the app the user was when they started the flow
   * so for example if they were on the home screen and clicked the button in
   * the empty bills widget it would be "home_screen_empty_bills_widget"
   */
  InsuranceLinkingStarted: {
    source: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  FinishedInsuranceLinking:
    | {
        linkType: Endpoint;
        status: "ERROR";
        errorCode: ExitError;
        linkCategory?: "INSURANCE" | "EHR";
      }
    | {
        linkType: Endpoint;
        status: "SUCCESS";
        linkCategory?: "INSURANCE" | "EHR";
      }
    | {
        linkType: Endpoint;
        status: "EXIT";
        linkCategory?: "INSURANCE" | "EHR";
      };
  InsuranceLinkAlternatePlan: { linkCategory?: "INSURANCE" | "EHR" };
  InsuranceLinkErrorStep: {
    step: InsuranceLinkPage;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceLinkBackStep: {
    initialRoute: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceLinkCloseStep: {
    initialRoute: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceLinkSkipped: {
    source: string;
    reason: "SKIP_BUTTON" | "CHAT_SKIP_BUTTON";
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceLinkExitSurveyOpened: { linkCategory?: "INSURANCE" | "EHR" };
  InsuranceLinkExitSurvey: {
    reason: string[];
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceLinkExitSurveyExtra: {
    details: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  ClickedConnectInsurance: {
    linkType: Endpoint;
    insuranceName: string | undefined | null;
    linkCategory?: "INSURANCE" | "EHR";
  };
  ClickedAddInsurance: {
    linkType: Endpoint;
    linkCategory?: "INSURANCE" | "EHR";
  };
  SkipInsuranceLinkOnboarding: {
    onboardingVersion?: OnboardingFlowVersion;
    /** @deprecated Use `onboardingVersion` instead. */
    isNewOnboarding?: boolean;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceLinkOnboardingBackStep: {
    initialRoute: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  SelectedInsuranceType: { linkCategory?: "INSURANCE" | "EHR" };
  // When the user presses yes on the loginInfoReady screen confirming they have
  // their credentials ready
  InsuranceLoginInfoYesPressed: { linkCategory?: "INSURANCE" | "EHR" };
  // When get help sheet is opened on loginInfoReady screen
  InsuranceLoginHelpSheetOpened: {
    insuranceName: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceLoginHelpSheetLinkClicked: {
    insuranceName: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceHelpSheetEmailSupportClicked: {
    insuranceName: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceHelpSheetResetLoginInformationClicked: {
    insuranceName: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceHelpSheetRegisterAccountClicked: {
    insuranceName: string;
    linkCategory?: "INSURANCE" | "EHR";
  };
  InsuranceHelpSheetViewGuideClicked: {
    insuranceName: string;
    guideUrl: string;
    linkCategory?: "INSURANCE" | "EHR";
  };

  // Fake Door MMFA — tracks every user action through the experiment flow
  FakeDoorMmfaViewed: FakeDoorMmfaEventBase & {
    step: "success_linked" | "claim_monitoring" | "email_action" | "waitlist";
  };
  FakeDoorMmfaContinued: FakeDoorMmfaEventBase & {
    step: "success_linked" | "claim_monitoring" | "email_action";
  };
  FakeDoorMmfaSkipped: FakeDoorMmfaEventBase & {
    step: "claim_monitoring" | "email_action";
  };
  FakeDoorMmfaDismissed: FakeDoorMmfaEventBase & {
    step: "success_linked" | "claim_monitoring" | "email_action" | "waitlist";
  };
  FakeDoorMmfaCompleted: FakeDoorMmfaEventBase;

  // User created tracking application installs/signups
  UserCreated: { email: string };
  // User authenticated for the first time following a signup
  UserFirstAuthentication: { email: string | undefined };

  // Hipaa Release Events
  HipaaReleasePersonMatched: {
    useMatchedPatient: boolean;
  };

  // Onboarding Events
  OnboardingFlowStepForward: {
    from?: string;
    to: string;
    onboardingVersion?: OnboardingFlowVersion;
    /** @deprecated Use `onboardingVersion` instead. */
    isNewOnboarding?: boolean;
  };
  OnboardingFlowStepBackward: {
    from?: string;
    to: string;
    onboardingVersion?: OnboardingFlowVersion;
    /** @deprecated Use `onboardingVersion` instead. */
    isNewOnboarding?: boolean;
  };

  // Bills
  OpenedBill: { id: string };
  ClickedHighPricePill: {
    eobId: string;
    percentageHigher: number;
    comparisonCount: number;
  };

  // Feedback
  FeedbackProvided: { rating: number };
  FeedbackDeepLink:
    | { result: "SUCCESS" }
    | { result: "ERROR"; error: "CASE_OPEN" };

  // Review
  /** Fired when the user initiates a review request */
  ReviewRequestInitiated: Record<string, never>;
  /** Fired when the user completes a review request */
  ReviewRequestCompleted: Record<string, never>;

  // Error
  ErrorScreenDisplayed: {
    screen: "NETWORK" | "BUG";
    errorType: string;
    message: string;
  };

  // Case Intake
  /** Should be fired any time we open the case intake flow
   * source is where in the app the user was when they started the flow
   * so for example if they were on the case list screen and clicked the button in
   * the empty case list it would be "cases_screen_empty_case_list"
   */
  CaseIntakeStarted: { source: string };
  CaseIntakeFlowStepForward: { to: string };
  CaseIntakeFlowStepBackward: { from: string; to: string };
  CaseIntakeFlowExit: { from: string | undefined };
  CaseIntakeCaseTypeSelection: { selection: string };
  CaseIntakeCaseSubTypeSelection: { type: string; selection: string };
  CaseIntakeSubmissionError: Record<string, never>;
  CaseIntakeSubmissionComplete: Record<string, never>;
  CaseIntakeSubmissionDuration: { duration: number };
  /** Fired when the floating create case button is pressed
   * The individual menu items are tracked separately with the CaseIntakeStarted event
   */
  FloatingCreateCaseButtonOpened: Record<string, never>;
  /** Fired when the user is prompted to select a plan for a case and they click
   * one of the options */
  PlanSelectedForPlanSelectionEvent: { planId: string };

  // Web Onboarding Sign up
  // Screen 2/3 of the web sign up onboarding flow
  SignUpOnboardingUserNeedsCompleted: {
    selections: string[];
    onboardingVersion?: OnboardingFlowVersion;
    /** @deprecated Use `onboardingVersion` instead. */
    isNewOnboarding?: boolean;
  };
  // Screen 3/3 of the web sign up onboarding flow
  SignUpOnboardingUsageScopeCompleted: {
    selections: string[];
    onboardingVersion?: OnboardingFlowVersion;
    /** @deprecated Use `onboardingVersion` instead. */
    isNewOnboarding?: boolean;
  };
  // Screen 1/3 of the web sign up onboarding flow
  SignUpOnboardingUserFeaturesCompleted: {
    selections: string[];
    onboardingVersion?: OnboardingFlowVersion;
    /** @deprecated Use `onboardingVersion` instead. */
    isNewOnboarding?: boolean;
  };
  SignUpOnboardingFlowStarted: {
    onboardingVersion?: OnboardingFlowVersion;
  };

  // Post-onboarding referral source tracking
  PostOnboardingReferralSource: {
    sources: string[];
    moreDetails?: string;
  };

  // Findings
  CreateIssueFromFindings: { title: string };
  OpenFindingsModal: { id: string };

  // Bot Control : integrated with the (web-only) login page
  // Published each time the widget is presented to a user
  BotControlChallengeIssued: Record<string, never>;
  // Published each time the challenge is solved
  // (whether automatically or with interactive checkbox)
  BotControlChallengeSolved: Record<string, never>;
  // Published each time the challenge expires
  // (5 minutes after the challenge is issued)
  BotControlChallengeExpired: Record<string, never>;
  // Published for any challenge that results in an error
  // https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
  BotControlChallengeError: { errorCode: string };

  /** Fired when the user clicks the continue button in the GetStartedSheet if
   * they have a benefits summary case */
  OpenedBenefitsSummary: Record<string, never>;

  // Home Screen V2 Analytics
  /** Fired when an issue is tapped in the IssuesWithActionsWidget */
  IssueActionTapped: {
    caseType: string;
    source: "ISSUE_ROW" | "ACTION_BUTTON";
    actionType?: string;
  };
  /** Fired when the Review now button is tapped in the ClaimsReviewWidget */
  ClaimsReviewTapped: {
    action: "REVIEW_NOW" | "UPLOAD_BILL";
  };

  // Promo Code
  PromoCodeExpand: { issueId: string };
  PromoCodeApply: { issueId: string; code: string };
  PromoCodeCancel: { issueId: string };

  // Download App Sheet (web-only iOS prompt)
  /** Fired when the download app sheet is displayed to the user */
  DownloadAppSheetShown: Record<string, never>;
  /** Fired when the user clicks the download button in the sheet */
  DownloadAppSheetClicked: Record<string, never>;
  /** Fired when the user dismisses the download app sheet */
  DownloadAppSheetDismissed: Record<string, never>;

  // Cases panel & list analytics
  /** Fired when the cases slide panel is opened or closed */
  CasesPanelToggled: {
    action: PanelToggleAction;
    source: PanelSource;
  };
  /** Fired when a filter checkbox changes in the CaseListFilterPopover (v2 UI) */
  CasesFilterChanged: {
    filterType: CaseListFilterType;
    enabled: boolean;
    resultingFilters: CaseListFilters;
  };
  /** Fired when the back button is pressed on the case page (mobile web) */
  CaseScreenBackButtonPressed: Record<string, never>;
  /** Fired when the case details modal is opened */
  CaseDetailsOpened: { issueId: string };

  // Post-escalation card
  /** Fired when a post-escalation action is clicked */
  PostEscalationActionTapped: {
    issueId: string;
    actionType: string;
  };
  /** Fired when the post-escalation actions list is expanded */
  PostEscalationActionsExpanded: { issueId: string };
  /** Fired when the post-escalation actions list is collapsed */
  PostEscalationActionsCollapsed: { issueId: string };

  // Bill Pay v2 — funnel for the BillPayAgreementModal flow. Every event
  // extends BillPayEventBase. Outcomes (accept / decline) come from
  // `BillPayRequestedEvent.acceptedAt` directly — we don't mirror backend
  // state into client analytics.
  /** Fired when the "Start saving" CTA on the BillPayRequestedEvent task card is tapped. */
  BillPayClickedStart: BillPayEventBase;
  /** Fired when the user advances from one bill-pay agreement step to the next. */
  BillPayAgreementStepForward: BillPayEventBase & {
    from: BillPayStepId;
    to: BillPayStepId;
  };
  /** Fired when the user goes back to a previous bill-pay agreement step. */
  BillPayAgreementStepBack: BillPayEventBase & {
    from: BillPayStepId;
    to: BillPayStepId;
  };
  /** Fired when the bill-pay agreement modal closes before the user accepts. */
  BillPayAgreementDismissed: BillPayEventBase & { step: BillPayStepId };
  /** Fired when the user opens the payment-method editor inside the bill-pay flow. */
  BillPayClickedEditPaymentMethod: BillPayEventBase;
  /** Fired when the user toggles the savings-preference radio. Captures behavior
   *  mid-flow — the final value also rides on `BillPayClickedPreAuthorize`. */
  BillPayChangedSavingsPreference: BillPayEventBase & {
    value: "any" | "minimum";
  };
  /** Fired when the user picks a minimum-discount tile (only meaningful when
   *  savingsPreference === "minimum"). Logs each pick, including re-picks. */
  BillPayChangedMinimumDiscount: BillPayEventBase & { value: number };
  /** Fired when the user adds or removes an alternative-strategy option. */
  BillPayChangedAlternativeStrategy: BillPayEventBase & {
    value: AlternativeStrategy;
    action: "add" | "remove";
  };
  /** Fired when the user picks a success-fee tier (10/15/20%). */
  BillPayChangedSuccessFee: BillPayEventBase & { value: 10 | 15 | 20 };
  /** Fired when the user taps "Pre-authorize payment" on the success-fee step.
   *  Carries the final settled values for self-describing terminal analytics;
   *  use the `BillPayChanged…` events for behavior leading up to this point.
   *  `minimumDiscountPercent` is null when `savingsPreference === "any"` to
   *  distinguish "didn't pick a threshold" from a numeric threshold. */
  BillPayClickedPreAuthorize: BillPayEventBase & {
    successFeePercent: number;
    savingsPreference: "any" | "minimum";
    minimumDiscountPercent: number | null;
  };
};

export type AttributionTraits = {
  // UTM parameters
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  utm_id?: string;
  utm_source_platform?: string; // sometimes included by Google Ads
  utm_creative_format?: string; // creative type
  utm_marketing_tactic?: string; // extended tracking

  // Self-attributing network (SAN) click IDs
  gclid?: string; // Google Ads
  wpkwn?: string; // Google Ad words
  gbraid?: string; // Google iOS app conversions
  wbraid?: string; // Google iOS web-to-app conversions
  fbclid?: string; // Facebook
  ttclid?: string; // TikTok
  twclid?: string; // Twitter
  li_fat_id?: string; // LinkedIn
  ScCid?: string; // Snapchat
  irclickid?: string; // Impact
  msclkid?: string; // Microsoft Ads
  yclid?: string; // Yandex Ads
  vcid?: string; // Verizon / Yahoo Ads

  // Apple Search Ads
  asa?: string;
  asa_campaign?: string;
  asa_adgroup?: string;
  asa_keyword?: string;
  // Apple Search Ads V2
  campaignId?: string;
  adgroupId?: string;
  keywordId?: string;
  creativeSetId?: string;

  // Complete Meta
  fbadid?: string;
  fbadname?: string;
  fbadsetid?: string;
  fbadsetname?: string;
  fbcampaignid?: string;
  fbcampaignname?: string;

  // Complete Instagram (Old)
  igshid?: string;
  ig_rid?: string;
  ig_mid?: string;

  // Other common attribution parameters
  referrer?: string;
  ref?: string;
  source?: string;
  ref_id?: string; // affiliate/referral code
  pid?: string; // affiliate partner ID
  cid?: string; // campaign/customer ID

  // Store when attribution was first captured
  attribution_timestamp?: string;
};

export type UserTraits = {
  name?: string;
  email?: string;
  isInternal?: boolean;
  userSegmentFeatures?: string[];
};

export type ScreenViewProperties = Record<string, string | string[]>;

// Define events to be sent to third-party tracking software listed in the
// trackingConfig. Custom events can use specific configurations if necessary.
export const eventTrackingConfig: {
  [E in keyof EventTypes]?: ApiOptions;
} = {
  UserCreated: trackingConfig, // Mapped to Signed Up properties: {email} preserve in transformation
  CreateIssue: trackingConfig, // Mapped to View Content (properties: { title } is purged in transformation
  // Add more events with custom configs as needed
};

// Insurance-related event names for use with useAccountLinkAnalytics hook
export type InsuranceEventNames =
  | "WebAgentCredentials"
  | "WebAgentMfaSelection"
  | "WebAgentSecurityQuestion"
  | "WebAgentMfaCode"
  | "WebAgentResendMfaCode"
  | "WebAgentManagedMfaApproval"
  | "FlexpaCheckSupport"
  | "FlexpaUnsupportedPlan"
  | "InsuranceLinkingStarted"
  | "FinishedInsuranceLinking"
  | "InsuranceLinkAlternatePlan"
  | "InsuranceLinkErrorStep"
  | "InsuranceLinkBackStep"
  | "InsuranceLinkCloseStep"
  | "InsuranceLinkSkipped"
  | "InsuranceLinkExitSurveyOpened"
  | "InsuranceLinkExitSurvey"
  | "InsuranceLinkExitSurveyExtra"
  | "ClickedConnectInsurance"
  | "ClickedAddInsurance"
  | "SkipInsuranceLinkOnboarding"
  | "InsuranceLinkOnboardingBackStep"
  | "SelectedInsuranceType"
  | "InsuranceLoginInfoYesPressed"
  | "InsuranceLoginHelpSheetOpened"
  | "InsuranceLoginHelpSheetLinkClicked"
  | "InsuranceHelpSheetEmailSupportClicked"
  | "InsuranceHelpSheetResetLoginInformationClicked"
  | "InsuranceHelpSheetRegisterAccountClicked"
  | "InsuranceHelpSheetViewGuideClicked"
  | "FakeDoorMmfaViewed"
  | "FakeDoorMmfaContinued"
  | "FakeDoorMmfaSkipped"
  | "FakeDoorMmfaDismissed"
  | "FakeDoorMmfaCompleted";

export interface AnalyticsServiceInterface {
  initialize(config: AnalyticsConfig): Promise<void>;
  trackEvent<E extends keyof EventTypes>(
    eventName: E,
    attributes: EventTypes[E],
  ): void;
  identifyUser(userId: string, traits: UserTraits): void;
  identifyAnonymousUser(traits: AttributionTraits): void;
  resetUser(): void;
  trackScreenView(screenName: string, properties: ScreenViewProperties): void;
  setAnonymousId(anonymousId: string): void;
  getAnonymousId(): string | undefined;
}
